import { useMemo } from 'react'

import { getNavigationForRole, type NavItem, type BadgeSource } from '@/config/navigation-config'

import { usePermissions } from '../hr/use-permissions'
import { useMyTasks } from '../pm/use-my-tasks'
import { useCurrentUser } from '../system/use-auth'

export interface NavItemWithBadge extends NavItem {
  badgeCount?: number
  children?: NavItemWithBadge[]
}

interface BadgeCounts {
  myTasks: number
  pendingApprovals: number
  pendingLeaves: number
  pendingExpenses: number
  pendingDevices: number
}

function getBadgeCount(source: BadgeSource | undefined, counts: BadgeCounts): number | undefined {
  if (!source) {
    return undefined
  }
  switch (source) {
    case 'my-tasks':
      return counts.myTasks > 0 ? counts.myTasks : undefined
    case 'pending-approvals':
      return counts.pendingApprovals > 0 ? counts.pendingApprovals : undefined
    case 'pending-leaves':
      return counts.pendingLeaves > 0 ? counts.pendingLeaves : undefined
    case 'pending-expenses':
      return counts.pendingExpenses > 0 ? counts.pendingExpenses : undefined
    case 'pending-devices':
      return counts.pendingDevices > 0 ? counts.pendingDevices : undefined
    default:
      return undefined
  }
}

function addBadgesToItems(items: NavItem[], counts: BadgeCounts): NavItemWithBadge[] {
  return items.map((item) => ({
    ...item,
    badgeCount: getBadgeCount(item.badgeSource, counts),
    children: item.children ? addBadgesToItems(item.children, counts) : undefined,
  }))
}

export function useRoleNavigation(): { items: NavItemWithBadge[]; isLoading: boolean } {
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const { hasPermission, hasRole } = usePermissions()
  const { data: myTasksData, isLoading: tasksLoading } = useMyTasks({ pageSize: 1 })
  const myTasksCount = myTasksData?.summary?.total ?? 0

  const items = useMemo(() => {
    if (!user) {
      return []
    }
    const sortedItems = getNavigationForRole(user.role, hasPermission, hasRole)
    const counts: BadgeCounts = {
      myTasks: myTasksCount,
      pendingApprovals: 0,
      pendingLeaves: 0,
      pendingExpenses: 0,
      pendingDevices: 0,
    }
    return addBadgesToItems(sortedItems, counts)
  }, [user, hasPermission, hasRole, myTasksCount])

  return { items, isLoading: userLoading || tasksLoading }
}
